import { closeMonthOf, countsInHeadcount } from './cost.js';
import { assumptionGridKeys, COLUMN_FIELDS, getDistinctValues, getEmpField } from './fields.js';
import { tsaById, tsaWrongSideLinks } from './economics.js';
import { parseDedication } from './project-file.js';
// Derived rather than restated: a second list of which columns are required is a second thing that can
// disagree with the import gate about whether a file is usable.
const REQUIRED_COLUMNS = COLUMN_FIELDS.filter((c) => c.required);
function isBlank(v) {
    return v === null || v === undefined || String(v).trim() === '';
}
const plural = (n, one, many = `${one}s`) => (n === 1 ? one : many);
export function computeDiagnostics(state) {
    const m = state.columnMap ?? {};
    const rows = state.rows ?? [];
    const emps = state.employees;
    const all = [...emps, ...state.design.buildUps];
    const errors = [];
    const warnings = [];
    const assumptions = [];
    const info = [];
    for (const c of REQUIRED_COLUMNS) {
        if (!m[c.key]) {
            errors.push({
                title: `Required field "${c.label}" is not mapped`,
                detail: `No Excel column maps to ${c.label}; the chart may be incomplete or mis-grouped.`,
            });
        }
    }
    if (!m.fte)
        assumptions.push({ title: 'No FTE column', detail: 'Every person is counted as 1.0 FTE.' });
    /*
      TWO REASONS THERE IS NO SALARY, AND THE READER NEEDS TO KNOW WHICH.
  
      A file with no salary column is the CLIENT'S doing and is fixable by re-importing with the column
      mapped. A grant that withholds compensation is the deployment's doing and is fixable only by an
      owner. Reporting the second under the first's sentence would send somebody to check a spreadsheet
      that is perfectly correct — and would say the data is missing when what is true is that it is not
      theirs to see.
  
      Checked first because it is the more specific answer: a withheld member on a project that also has
      no salary column should be told the thing they can act on, which is neither, so the honest one is
      the one about access.
    */
    if (state.scope?.salaryWithheld) {
        assumptions.push({
            title: 'Compensation is not available to you',
            detail: 'Your access to this project does not include salary, so cost figures are 0 and the cost ' +
                'panes are not shown. The data is there; ask an owner if you need it.',
        });
    }
    else if (!m.salary) {
        assumptions.push({ title: 'No Salary column', detail: 'All salary / cost figures are 0.' });
    }
    if (!state.groupingLevels.length) {
        info.push({
            title: 'No hierarchy levels defined',
            detail: 'Everyone shows directly under the root. Add levels via the column-mapping screen.',
        });
    }
    const mappedCols = new Set(Object.values(m));
    const extra = (state.excelColumns ?? []).filter((c) => !mappedCols.has(c));
    if (extra.length) {
        info.push({
            title: `${extra.length} unused ${plural(extra.length, 'column')} available for Group by / Filters`,
            detail: extra.join(', '),
        });
    }
    if (m.id) {
        const noId = rows.filter((r) => isBlank(r[m.id])).length;
        if (noId) {
            warnings.push({
                title: `${noId} ${plural(noId, 'row')} with no Personnel ID`,
                detail: 'Assigned auto-generated IDs. Bucket / head decisions for these may not survive a data update.',
            });
        }
    }
    else {
        assumptions.push({
            title: 'No Personnel ID column',
            detail: 'Row order is used as identity; data updates cannot reliably match people.',
        });
    }
    const counts = Object.create(null);
    for (const e of all)
        counts[e.id] = (counts[e.id] ?? 0) + 1;
    const dupes = Object.entries(counts).filter(([, n]) => n > 1);
    if (dupes.length) {
        warnings.push({
            title: `${dupes.length} duplicate Personnel ${plural(dupes.length, 'ID')}`,
            detail: `e.g. ${dupes.slice(0, 5).map(([id, n]) => `${id} (×${n})`).join(', ')}. Buckets & heads are keyed by ID and may collide.`,
        });
    }
    if (m.salary) {
        const bad = rows.filter((r) => !isBlank(r[m.salary]) && !Number.isFinite(Number(r[m.salary]))).length;
        if (bad) {
            warnings.push({
                title: `${bad} non-numeric Salary ${plural(bad, 'value')}`,
                detail: 'Treated as 0 in cost figures.',
            });
        }
    }
    if (m.fte) {
        const bad = rows.filter((r) => !isBlank(r[m.fte]) && !Number.isFinite(Number(r[m.fte]))).length;
        if (bad) {
            warnings.push({ title: `${bad} non-numeric FTE ${plural(bad, 'value')}`, detail: 'Treated as 0.' });
        }
        const odd = all.filter((e) => (e.fte ?? 0) > 1 || (e.fte ?? 0) < 0).length;
        if (odd) {
            info.push({
                title: `${odd} ${plural(odd, 'person', 'persons')} with FTE > 1 or < 0`.replace('persons', 'people'),
                detail: 'Verify these are intentional (e.g. multiple roles or data errors).',
            });
        }
    }
    for (const l of state.groupingLevels) {
        const n = all.filter((e) => isBlank(getEmpField(e, l.key, 'as-is', undefined))).length;
        if (n) {
            assumptions.push({
                title: `${n} ${n === 1 ? 'person' : 'people'} with no ${l.label}`,
                detail: 'Ragged hierarchy: they sit directly under their parent-level group.',
            });
        }
    }
    if (m.dedication) {
        const cells = rows.map((r) => r[m.dedication]).filter((v) => !isBlank(v));
        const legacyN = cells.filter((v) => parseDedication(v).legacyMarker).length;
        const clampedN = cells.filter((v) => {
            const num = Number(String(v).replace('%', '').replace(',', '.'));
            return Number.isFinite(num) && (num < 0 || num > 100);
        }).length;
        const missing = emps.filter((e) => e.dedication == null && !e.sharedMarker).length;
        if (legacyN) {
            info.push({
                title: `${legacyN} non-numeric Dedication ${plural(legacyN, 'value')}`,
                detail: 'Treated as legacy boolean shared markers (◐, no numeric dedication) — the perimeter threshold rule skips them.',
            });
        }
        if (clampedN) {
            warnings.push({
                title: `${clampedN} out-of-range Dedication ${plural(clampedN, 'value')}`,
                detail: 'Values are clamped into 0–100%.',
            });
        }
        if (missing) {
            assumptions.push({
                title: `${missing} ${missing === 1 ? 'person' : 'people'} without Dedication`,
                detail: 'Unknown dedication: the perimeter threshold rule skips them; designate them manually if needed.',
            });
        }
    }
    else {
        assumptions.push({
            title: 'No Dedication column',
            detail: 'The perimeter threshold rule is disabled — designate carve-out membership manually (per person, group or multi-select), or enter dedication values in the side panel.',
        });
    }
    /*
      A severance tier that misses its population only by case or whitespace.
  
      `severanceMonths` matches grid keys with `hasOwn` — exact and case-sensitive (cost.ts) — while the
      two spellings arrive from sides nobody reconciles: the stored grid survives re-imports and data
      updates, and the roster's casing can change under it. A grid saying "germany" against a roster
      saying "Germany" prices NOBODY, and every affected person silently inherits the next tier down —
      a wrong number that reads as right. The assumptions editor already tags such a key as an orphan
      tier; it is named here as well because the editor is not where anyone verifies a SteerCo figure.
  
      Keys that match nobody AT ALL are deliberately not flagged: a grid legitimately keeps tiers for
      countries that left the roster on a refresh, and the editor's orphan tagging covers those.
    */
    for (const [field, grid, noun, kind, verb] of [
        ['country', state.assumptions.severanceByCountry, 'severance country', 'Severance', 'prices'],
        ['seniority', state.assumptions.severanceBySeniority, 'severance seniority', 'Severance', 'prices'],
        // Same trap, third grid: a 'france' lead time against a 'France' roster delays nobody.
        ['country', state.assumptions.consultationMonthsByCountry, 'consultation country', 'Consultation', 'delays'],
        ['country', state.assumptions.loadedCostMultiplierByCountry, 'loaded-cost country', 'Loaded-cost', 'reprices'],
        ['country', state.assumptions.closeMonthByCountry, 'close-month country', 'Close-month', 'defers'],
    ]) {
        // The editor's own key source, so what this check compares against and what the editor offers to
        // rename cannot drift apart.
        const population = assumptionGridKeys(state, field);
        const exact = new Set(population);
        const folded = new Map(population.map((v) => [v.trim().toLowerCase(), v]));
        const near = Object.entries(grid)
            // A blank tier prices nothing whatever its key says, so a near-miss on one changes no figure.
            .filter(([, value]) => value != null && String(value).trim() !== '')
            .filter(([key]) => !exact.has(key))
            .flatMap(([key]) => {
            const hit = folded.get(key.trim().toLowerCase());
            return hit == null ? [] : [{ key, hit }];
        });
        if (near.length) {
            warnings.push({
                title: `${near.length} ${noun} ${plural(near.length, 'tier')} missing the roster by case or spacing`,
                detail: `${kind} tiers match exactly: ` +
                    near
                        .slice(0, 5)
                        .map(({ key, hit }) => `"${key}" ${verb} nobody while the roster says "${hit}"`)
                        .join('; ') +
                    `${near.length > 5 ? '; …' : ''}. Rename or clear the tier in Assumptions so the override applies.`,
            });
        }
    }
    /*
      Rightsizings scheduled inside their country's consultation lead time.
  
      A WARNING, deliberately not an enforcement: the plan editor stays free to record the month the
      lawyers are still negotiating, and this names the gap where numbers get verified — the same
      posture as the over-removal metric. Gated on the grid carrying anything at all, so every project
      that has never stated a lead time stays byte-identical (the parity harness holds this output
      verbatim against a frozen app with no such concept).
    */
    {
        const grid = state.assumptions.consultationMonthsByCountry;
        const leadOf = (country) => {
            if (country == null || country === '')
                return null;
            if (!Object.prototype.hasOwnProperty.call(grid, country))
                return null;
            const raw = grid[country];
            if (raw == null || String(raw).trim() === '')
                return null;
            const months = Number(raw);
            return Number.isFinite(months) && months > 0 ? months : null;
        };
        if (Object.keys(grid).length) {
            const early = emps.flatMap((e) => {
                if (!Object.prototype.hasOwnProperty.call(state.design.strandedPlan, e.id))
                    return [];
                const plan = state.design.strandedPlan[e.id];
                if (plan.action !== 'rightsize')
                    return [];
                const lead = leadOf(e.country);
                return lead != null && plan.month < lead ? [{ e, month: plan.month, lead }] : [];
            });
            if (early.length) {
                warnings.push({
                    title: `${early.length} ${plural(early.length, 'rightsizing')} inside the consultation lead time`,
                    detail: early
                        .slice(0, 5)
                        .map(({ e, month, lead }) => `${e.name} (${e.country}): planned M${month}, consultation runs ${lead} months`)
                        .join('; ') +
                        `${early.length > 5 ? '; …' : ''}. The timeline is made by consultation, not design — ` +
                        `move the plan month or challenge the lead time in Assumptions.`,
                });
            }
        }
    }
    /*
      Countries whose consultation cannot fit between Day 1 and their own deferred close.
  
      A deferred closing buys integration time, not consultation time: transfer consultation must
      complete before the LOCAL close, and a country closing at M3 with a six-month lead time needs
      its clock started three months before Day 1 — which is exactly the kind of pre-close work this
      model cannot see and therefore must name. Gated on both grids carrying something, so every
      project without the two concepts stays byte-identical (the parity bargain, again).
    */
    {
        const closes = state.assumptions.closeMonthByCountry;
        const leads = state.assumptions.consultationMonthsByCountry;
        // The LEADS grid is parsed locally, like `leadOf` above — it has no canonical accessor. The
        // CLOSE grid is deliberately not: `closeMonthOf` is the one door every engine and panel reads
        // through (rounding included), and a warning that names a close month no other surface shows
        // would be the drift the door exists to prevent.
        const leadFor = (key) => {
            if (!Object.prototype.hasOwnProperty.call(leads, key))
                return null;
            const raw = leads[key];
            if (raw == null || String(raw).trim() === '')
                return null;
            const n = Number(raw);
            return Number.isFinite(n) && n > 0 ? n : null;
        };
        if (Object.keys(closes).length && Object.keys(leads).length) {
            const heads = new Map();
            for (const e of emps) {
                if (e.isLeaver || !countsInHeadcount(e))
                    continue;
                if (e.country == null || String(e.country).trim() === '')
                    continue;
                if (!Object.prototype.hasOwnProperty.call(state.design.buckets, e.id))
                    continue;
                if (state.design.buckets[e.id] !== 'Carve-out')
                    continue;
                const key = String(e.country);
                heads.set(key, (heads.get(key) ?? 0) + 1);
            }
            const squeezed = [...heads.entries()].flatMap(([country, n]) => {
                const close = closeMonthOf({ country }, state.assumptions);
                const lead = leadFor(country);
                return close > 0 && lead != null && lead > close
                    ? [{ country, n, close, lead }]
                    : [];
            });
            if (squeezed.length) {
                warnings.push({
                    title: `${squeezed.length} ${plural(squeezed.length, 'country', 'countries')} where consultation outruns the local close`,
                    detail: squeezed
                        .slice(0, 5)
                        .map(({ country, n, close, lead }) => `${country} (${n} ${plural(n, 'person', 'people')}): closes M${close}, consultation runs ` +
                        `${lead} months — start ${lead - close} ${plural(lead - close, 'month')} before Day 1`)
                        .join('; ') +
                        `${squeezed.length > 5 ? '; …' : ''}. A deferred closing buys no consultation time — ` +
                        `the clock must start before Day 1 or the local close slips.`,
                });
            }
        }
    }
    /*
      Mitigations planned before their carrier's country has even closed.
  
      Until the local close the business is still the seller's, and a carrier rightsized at M2 in a
      country that closes at M6 is someone paid off while still doing real work — severance booked
      for stranded cost that never started. The engine deliberately renders the plan as stated (zero
      accrual, severance at the stated month) rather than smoothing it; this names it. AT the close
      counts too: a close at k means the business transfers at the start of month k+1 (accrual runs
      m > k), so a plan effective FROM month k removes the carrier for the whole of seller-owned
      month k — k+1 is the earliest exit that is not premature. Gated on the close grid carrying
      anything at all, same bargain as every grid check above.
    */
    {
        const closes = state.assumptions.closeMonthByCountry;
        if (Object.keys(closes).length) {
            const premature = emps.flatMap((e) => {
                if (!Object.prototype.hasOwnProperty.call(state.design.strandedPlan, e.id))
                    return [];
                const plan = state.design.strandedPlan[e.id];
                if (plan.action !== 'rightsize' && plan.action !== 'redeploy')
                    return [];
                const close = closeMonthOf(e, state.assumptions);
                return close > 0 && plan.month <= close ? [{ e, plan, close }] : [];
            });
            if (premature.length) {
                warnings.push({
                    title: `${premature.length} ${plural(premature.length, 'mitigation')} planned before the local close`,
                    detail: premature
                        .slice(0, 5)
                        .map(({ e, plan, close }) => `${e.name} (${e.country}): ${plan.action} at M${plan.month}, but ${e.country} closes M${close}`)
                        .join('; ') +
                        `${premature.length > 5 ? '; …' : ''}. Until the local close the business is still the ` +
                        `seller's — move the plan past the close, or challenge the close month in Assumptions.`,
                });
            }
        }
    }
    /*
      Currencies the FX grid cannot price — the mixed-currency sum, named where numbers get verified.
  
      `fxFactor` treats a currency with no usable rate as × 1: correct for a single-currency roster
      (the identity case, which must stay silent — the parity harness holds this output verbatim
      against a frozen app that has no currency concept), and silently wrong the moment the roster
      mixes currencies or a grid says conversion is in play. So this speaks only when it has something
      true to say: two or more currencies present, or a grid already carrying a usable tier.
    */
    {
        const currencies = assumptionGridKeys(state, 'currency');
        const grid = state.assumptions.fxRates;
        const usable = (code) => Object.prototype.hasOwnProperty.call(grid, code) &&
            Number(grid[code]) > 0 &&
            Number.isFinite(Number(grid[code]));
        const anyUsableTier = Object.keys(grid).some(usable);
        if (currencies.length >= 2 || (currencies.length >= 1 && anyUsableTier)) {
            const unpriced = currencies.filter((c) => !usable(c));
            if (unpriced.length) {
                const heads = (code) => emps.filter((e) => !e.isLeaver && String(e.currency ?? '') === code && countsInHeadcount(e)).length;
                warnings.push({
                    title: `${unpriced.length} ${plural(unpriced.length, 'currency', 'currencies')} with no FX rate`,
                    detail: `Salaries in ${unpriced
                        .slice(0, 5)
                        .map((c) => `${c} (${heads(c)} ${plural(heads(c), 'person', 'people')})`)
                        .join(', ')}${unpriced.length > 5 ? ', …' : ''} are summed at × 1 into the reporting ` +
                        `currency. Add rates under Assumptions, or the totals mix currencies.`,
                });
            }
            // The severance near-miss, for currency codes: an 'usd' tier against a 'USD' roster converts
            // nobody, and unlike severance there is no inheritance chain to soften it — just × 1.
            const exact = new Set(currencies);
            const folded = new Map(currencies.map((v) => [v.trim().toLowerCase(), v]));
            const near = Object.keys(grid)
                .filter((key) => usable(key) && !exact.has(key))
                .flatMap((key) => {
                const hit = folded.get(key.trim().toLowerCase());
                return hit == null ? [] : [{ key, hit }];
            });
            if (near.length) {
                warnings.push({
                    title: `${near.length} FX ${plural(near.length, 'tier')} missing the roster by case or spacing`,
                    detail: `FX tiers match exactly: ` +
                        near
                            .slice(0, 5)
                            .map(({ key, hit }) => `"${key}" converts nobody while the roster says "${hit}"`)
                            .join('; ') +
                        `${near.length > 5 ? '; …' : ''}. Rename or clear the tier in Assumptions so the rate applies.`,
                });
            }
        }
    }
    /*
      The pension tripwire: who in the carve-out carries a scheme, counted and named.
  
      Not a model — pricing pensions is actuarial work — but on UK and German deals DB exposure
      routinely outstrips the whole severance envelope, and the difference between the actuaries
      getting this list in month two and in SPA-markup week is exactly whether anything counts it.
      A WARNING because it demands an action (send the list), not because the data is wrong; silent
      whenever nobody in the carve-out carries a scheme, which is every pre-pension project.
    */
    {
        const carriers = emps.filter((e) => !e.isLeaver &&
            countsInHeadcount(e) &&
            !isBlank(e.pensionScheme) &&
            Object.prototype.hasOwnProperty.call(state.design.buckets, e.id) &&
            state.design.buckets[e.id] === 'Carve-out');
        if (carriers.length) {
            const byScheme = new Map();
            for (const e of carriers) {
                const scheme = String(e.pensionScheme);
                byScheme.set(scheme, (byScheme.get(scheme) ?? 0) + 1);
            }
            const schemes = [...byScheme.entries()].sort((a, b) => b[1] - a[1]);
            warnings.push({
                title: `${carriers.length} carve-out ${plural(carriers.length, 'person', 'people')} carry a pension scheme`,
                detail: `Pension liabilities transfer with people: ` +
                    schemes
                        .slice(0, 5)
                        .map(([scheme, n]) => `${scheme} (${n})`)
                        .join(', ') +
                    `${schemes.length > 5 ? ', …' : ''}. Get this list to the actuaries before signing — DB ` +
                    `exposure routinely outstrips the severance envelope, and nothing here prices it.`,
            });
        }
    }
    // Stale references survive deletions harmlessly, but a silent leftover is still worth naming.
    const idSet = new Set(all.map((e) => String(e.id)));
    const orphanMeta = Object.keys(state.design.bucketMeta).filter((id) => !idSet.has(String(id))).length;
    if (orphanMeta) {
        info.push({
            title: `${orphanMeta} stage ${plural(orphanMeta, 'attribution')} for people no longer in the data`,
            detail: 'Harmless leftovers (e.g. from removed build-ups or data updates); they are ignored everywhere.',
        });
    }
    const dangling = state.design.buildUps.filter((b) => b.tsaId && !tsaById(state.design, b.tsaId)).length;
    if (dangling) {
        warnings.push({
            title: `${dangling} build-up TSA ${plural(dangling, 'link')} point at a removed TSA`,
            detail: 'Re-link the replacement in the build-up’s panel or the TSA register, or the exit timeline will not show it.',
        });
    }
    const wrongSide = tsaWrongSideLinks(state.design);
    if (wrongSide.length) {
        warnings.push({
            title: `${wrongSide.length} TSA ${plural(wrongSide.length, 'replacement')} on the wrong side`,
            detail: `Received TSAs are replaced by Carve-out (Day 2) build-ups; provided (reverse) TSAs by Remain build-ups — these links are ignored by coverage and economics until fixed: ` +
                `${wrongSide
                    .slice(0, 5)
                    .map((b) => `${b.name} → "${tsaById(state.design, b.tsaId)?.name ?? ''}"`)
                    .join('; ')}${wrongSide.length > 5 ? '; …' : ''}. Fix the build-up's bucket or its TSA link in the person panel.`,
        });
    }
    const stats = [
        { k: 'Employees', v: emps.length },
        { k: 'Source rows', v: rows.length },
    ];
    if (state.design.buildUps.length)
        stats.push({ k: 'Build-ups', v: state.design.buildUps.length });
    for (const l of state.groupingLevels) {
        stats.push({
            k: `Distinct ${l.label}`,
            v: new Set(all.map((e) => getEmpField(e, l.key, 'as-is', undefined) || `(No ${l.label})`)).size,
        });
    }
    return { errors, warnings, assumptions, info, stats };
}
/**
 * The checks that read `state.rows`, and are therefore silent when the source rows are not held.
 *
 * In the single-file app `state.rows` was always the spreadsheet the user had just dropped in, so every
 * check below always ran. On the platform the raw rows are deliberately NOT retained by default: an
 * unmapped column can carry a home address or a bank account, so the importer drops what it did not map
 * unless asked to keep it.
 *
 * That makes these particular checks unreportable rather than clean, and the difference matters — "no
 * non-numeric salaries" and "we did not look" are opposite answers for someone deciding whether to trust
 * a cost figure. `computeDiagnostics` cannot tell them apart from an empty array, so the surface that
 * renders it says which ones it could not run, and this function is the single list of them.
 *
 * Filtered by the column map, because a check on an unmapped column would not have run either way — its
 * absence is already reported as an assumption.
 */
export function sourceRowDependentChecks(state) {
    const m = state.columnMap ?? {};
    const checks = [];
    if (m.id)
        checks.push('Rows with no Personnel ID');
    if (m.salary)
        checks.push('Non-numeric Salary values');
    if (m.fte)
        checks.push('Non-numeric FTE values');
    if (m.dedication)
        checks.push('Non-numeric and out-of-range Dedication values');
    return checks;
}
/**
 * Reassemble `state.rows` from the rows the import chose to keep.
 *
 * A person's `raw` is their original spreadsheet row when the import was asked to retain it. That is not
 * the same thing as the source file — a row the importer skipped is not here, and neither is a person
 * added by a later data update — so the count comes back with the population to compare it against
 * instead of being presented as the file.
 *
 * Build-ups are excluded by construction: they never came from a row.
 */
export function retainedSourceRows(state) {
    const rows = [];
    for (const person of state.employees) {
        if (person.raw)
            rows.push(person.raw);
    }
    return { rows, retained: rows.length, population: state.employees.length };
}
/**
 * Which as-is legal entity each person's cost sits in today, and which one it moves to.
 *
 * Build-ups have no as-is entity, so they enter from a synthetic '(New hire)' source — a real
 * inflow rather than a phantom transfer out of nowhere.
 */
export function computeEntityFlows(state, perspective, fn = () => 1) {
    const map = Object.create(null);
    const srcTotals = Object.create(null);
    const tgtTotals = Object.create(null);
    for (const p of [...state.employees, ...state.design.buildUps]) {
        // Unfilled roles and inactive people carry no incumbent headcount or cost.
        if (!countsInHeadcount(p))
            continue;
        const v = fn(p);
        const src = p.isBuildUp
            ? '(New hire)'
            : String(getEmpField(p, 'legalEntity', 'as-is', undefined) ?? '').trim() || '(No entity)';
        const tgt = String(getEmpField(p, 'legalEntity', perspective, state.design.scenarioOverrides) ?? '').trim() ||
            '(No entity)';
        const row = map[src] ?? (map[src] = Object.create(null));
        row[tgt] = (row[tgt] ?? 0) + v;
        srcTotals[src] = (srcTotals[src] ?? 0) + v;
        tgtTotals[tgt] = (tgtTotals[tgt] ?? 0) + v;
    }
    const cmp = (a, b) => a.localeCompare(b);
    const sources = Object.keys(srcTotals).sort(cmp);
    const targets = Object.keys(tgtTotals).sort(cmp);
    const links = [];
    for (const s of sources) {
        for (const t of targets) {
            const v = map[s]?.[t];
            if (v)
                links.push({ source: s, target: t, value: v });
        }
    }
    return { sources, targets, srcTotals, tgtTotals, links };
}
/** A registry entity's classification; anything unregistered is assumed to be retained. */
export function entityClassOf(state, name) {
    const found = (state.entities ?? []).find((e) => e.name === name);
    return found ? found.classification : 'retain';
}
/** Registry names plus any value already used as a live target in this perspective. */
export function getEntityNames(state, perspective) {
    const names = new Set((state.entities ?? []).map((e) => e.name));
    for (const n of getDistinctValues(state, 'legalEntity', 'Legal Entity', perspective)) {
        if (n && !/^\(No /.test(n))
            names.add(n);
    }
    return [...names].sort((a, b) => a.localeCompare(b));
}
/*
  The attributes a refresh compares.

  The last three are additions, not a port. The original's `attrKeys` (orgchart.html:10241) omits
  supervisorId, legalEntity and country — but its apply step replaces `state.employees` wholesale, so
  all three ARE applied while being invisible to the preview, the diff and the change log. A refresh
  could therefore re-parent the entire reporting hierarchy and move every legal-entity rollup with no
  audit trail and nothing on screen to say so. Detecting them is the fix; the apply already did it.
*/
const DIFF_ATTRIBUTES = [
    'salary', 'fte', 'location', 'jobTitle', 'seniority', 'businessUnit', 'name',
    'openPosition', 'inactive', 'dedication',
    'supervisorId', 'legalEntity', 'country', 'currency', 'pensionScheme',
];
function attributeValue(p, key) {
    switch (key) {
        case 'openPosition':
            return p.openPosition;
        case 'inactive':
            return p.inactive;
        case 'dedication':
            return p.dedication;
        default:
            return p[key];
    }
}
/**
 * Compare the current roster against a refreshed extract.
 *
 * Existing leavers are excluded from the "before" side, so somebody already recorded as departed is
 * not reported as leaving twice. A mover's attribute changes are kept alongside their move: the
 * displayed categories are exclusive, but the change log needs everything that actually changed.
 */
export function computeDataDiff(state, oldEmps, newEmps) {
    const oldById = new Map(oldEmps.filter((e) => !e.isLeaver).map((e) => [String(e.id), e]));
    const newById = new Map(newEmps.map((e) => [String(e.id), e]));
    const groupKeys = state.groupingLevels.map((l) => l.key);
    const diff = {
        joiners: [], leavers: [], movers: [], attrChanges: [], unchanged: [], headsBroken: [],
    };
    const same = (a, b) => String(a ?? '') === String(b ?? '');
    for (const [id, ne] of newById) {
        const oe = oldById.get(id);
        if (!oe) {
            diff.joiners.push(ne);
            continue;
        }
        const groupChanges = groupKeys
            .filter((k) => !same(oe.levels[k], ne.levels[k]))
            .map((k) => ({ field: k, from: oe.levels[k], to: ne.levels[k] }));
        const attrChanged = DIFF_ATTRIBUTES.filter((k) => !same(attributeValue(oe, k), attributeValue(ne, k))).map((k) => ({ field: k, from: attributeValue(oe, k), to: attributeValue(ne, k) }));
        if (groupChanges.length) {
            diff.movers.push({ id, name: ne.name, changes: groupChanges, attrChanges: attrChanged });
        }
        if (attrChanged.length && !groupChanges.length) {
            diff.attrChanges.push({ id, name: ne.name, changes: attrChanged });
        }
        if (!groupChanges.length && !attrChanged.length)
            diff.unchanged.push(id);
    }
    for (const [id, oe] of oldById) {
        if (!newById.has(id))
            diff.leavers.push(oe);
    }
    // A head who has left breaks their group's designation, in every perspective that named them.
    const oldIds = new Set(oldById.keys());
    for (const [scenario, groupMap] of Object.entries(state.design.heads ?? {})) {
        for (const [groupId, empId] of Object.entries(groupMap ?? {})) {
            if (empId == null)
                continue; // an explicit "no head" marker cannot break
            if (!newById.has(String(empId)) && oldIds.has(String(empId))) {
                diff.headsBroken.push({ scenario, groupId, empId: String(empId) });
            }
        }
    }
    return diff;
}
//# sourceMappingURL=diagnostics.js.map